import "core-js/modules/es.array.push.js";
import { featureReduce } from '@turf/meta';
import union from '@turf/union';
import useIsMounted from 'app/hooks/useIsMounted';
import cx from 'classnames';
import ModalActionBar from "../../core/components/Modal/ModalActionBar";
import ModalHeader from "../../core/components/Modal/ModalHeader";
import { LimitType } from 'legoland-sdk/dist/experimental';
import { Preloader } from 'llui';
import moment from 'moment';
import { useAccessLimitsQuery, useLimitsMutation, useSharedLimitsQuery } from "../api/limitsApiHooks";
import { useProductLimitDefaults, useProductLimitSchemas } from "../productsHooks";
import { useCallback, useEffect, useState } from 'react';
import { Button, useToasts } from 'tombac';
import { useUserDetails } from "../../users/UserDetailsProvider";
import "./LimitsConfigurator.css";
import { ProductLogo } from "./ProductLogo";
import { getLimitEditorComponent } from "./limit-editors";
import { initLoadedGeoLimit } from "./limit-editors/GeoLimitEditor";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const convertCoords = limit => {
  const newCords = limit.features.map(feature => {
    const cords = feature.geometry.coordinates[0];
    const convertedCords = cords.map(pointCord => {
      const [lng, lat] = pointCord;
      return lng > 180 ? [lng - 360, lat] : lng < -180 ? [360 - Math.abs(lng), lat] : pointCord;
    });
    return {
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: [convertedCords]
      }
    };
  });
  return {
    ...limit,
    features: newCords
  };
};
const afterLoadProcessors = {
  /* Converts MultiPolygon limit to FeatureCollection consumed by GeoLimitEditor component. */
  [LimitType.Geo]: limit => {
    try {
      return initLoadedGeoLimit(limit);
    } catch (error) {
      console.error(error);
      return null;
    }
  }
};
const beforeSaveProcessors = {
  [LimitType.DateRange]: limit => limit && {
    from: limit?.from && moment(limit.from).format('YYYY-MM-DD'),
    to: limit?.to && moment(limit.to).format('YYYY-MM-DD')
  },
  /* Convert FeatureCollection output from GeoLimitEditor to MultiPolygon limit. */
  [LimitType.Geo]: limit => {
    if (limit) {
      const newLimit = convertCoords(limit);
      return featureReduce(newLimit, (prev, curr) => !prev ? curr : union(prev, curr))?.geometry ?? null;
    }
  }
};
function NoOpComponent(_ref) {
  let {
    children
  } = _ref;
  return children;
}
export default function LimitsConfigurator(_ref2) {
  let {
    className,
    onRequestClose,
    product
  } = _ref2;
  const mounted = useIsMounted();
  const editedUser = useUserDetails();
  const [saving, setSaving] = useState(false);
  const defaults = useProductLimitDefaults({
    accessType: product.accessType,
    productId: product.id
  });
  const schemas = useProductLimitSchemas({
    productId: product.id
  });
  const accessLimitsQuery = useAccessLimitsQuery({
    username: editedUser.email,
    productId: product.id
  });
  const {
    limits: loadedLimits
  } = accessLimitsQuery.data || {};
  const sharedLimitsQuery = useSharedLimitsQuery(undefined, {
    enabled: product.id === 'ts' || product.id === 'od'
  });
  const sharedLimits = sharedLimitsQuery.data?.limits;
  const [limits, setLimits] = useState(null);
  useEffect(() => setLimits(accessLimitsQuery.isLoading ? null : afterLoadLimits(loadedLimits) ?? {}), [loadedLimits, schemas]);
  const {
    mutateAsync: saveLimits
  } = useLimitsMutation();
  const handleLimitChange = (limitName, value) => setLimits({
    ...limits,
    [limitName]: value
  });
  const {
    addToast
  } = useToasts();
  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      await saveLimits({
        limits: beforeSaveLimits(limits),
        productId: product.id,
        username: editedUser.email
      });
      addToast('User limits saved', 'success');
      if (mounted.current) onRequestClose();
    } catch (error) {
      console.error(error);
      addToast(error?.message || 'Failed to save limits', 'danger');
    } finally {
      if (mounted.current) {
        setSaving(false);
      }
    }
  }, [limits, product]);
  function afterLoadLimits(limits) {
    if (!limits) return null;
    const processedLimits = {};
    schemas.forEach(_ref3 => {
      let {
        name: limitName,
        type: limitType
      } = _ref3;
      const limit = limits[limitName];
      processedLimits[limitName] = limit && afterLoadProcessors[limitType] ? afterLoadProcessors[limitType](limit) : limit;
    });
    return processedLimits;
  }
  const validateRegions = () => {
    const allowedArea = limits?.allowedArea;
    if (allowedArea) {
      return allowedArea.features.some(feature => feature.properties.isCrossedMeridian === true);
    }
    return false;
  };
  function beforeSaveLimits(limits) {
    if (!limits) return null;
    const processedLimits = {};
    schemas.forEach(_ref4 => {
      let {
        name: limitName,
        type: limitType
      } = _ref4;
      if (sharedLimits && Object.hasOwn(sharedLimits, limitName)) return;
      const limit = limits[limitName];
      processedLimits[limitName] = beforeSaveProcessors[limitType] ? beforeSaveProcessors[limitType](limit) : limit;
    });
    return processedLimits;
  }
  const limitComponents = [];
  let ExtendedEditor = null;
  let extendedEditorProps;
  let EditorContextProvider = NoOpComponent;
  if (limits) {
    schemas.forEach(schema => {
      const LimitComponent = getLimitEditorComponent(schema.type);
      limitComponents.push(LimitComponent);
      if (LimitComponent.ExtendedEditor) {
        ExtendedEditor = LimitComponent.ExtendedEditor;
        extendedEditorProps = {
          defaultLimit: defaults[schema.name],
          label: schema.label,
          limit: limits[schema.name],
          onChange: value => handleLimitChange(schema.name, value),
          schema
        };
      }
      if (LimitComponent.ContextProvider) {
        EditorContextProvider = LimitComponent.ContextProvider;
      }
    });
  }
  const loading = !limits || sharedLimitsQuery.isLoading;
  return /*#__PURE__*/_jsx("section", {
    className: cx('LimitsConfigurator', loading && 'LimitsConfigurator--loading', ExtendedEditor && 'LimitsConfigurator--extended', className),
    "data-sentry-component": "LimitsConfigurator",
    "data-sentry-source-file": "LimitsConfigurator.tsx",
    children: /*#__PURE__*/_jsxs(EditorContextProvider, {
      "data-sentry-element": "EditorContextProvider",
      "data-sentry-source-file": "LimitsConfigurator.tsx",
      "data-sentry-element": "EditorContextProvider",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "LimitsConfigurator__main-pane",
        children: [/*#__PURE__*/_jsx(ModalHeader, {
          image: /*#__PURE__*/_jsx(ProductLogo, {
            productId: product.id
          }),
          primaryTitle: "Set usage limits",
          secondaryTitle: product.displayName,
          tag: "header",
          "data-sentry-element": "ModalHeader",
          "data-sentry-source-file": "LimitsConfigurator.tsx",
          "data-sentry-element": "ModalHeader"
        }), loading ? /*#__PURE__*/_jsx(Preloader, {}) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("ul", {
            className: "LimitsConfigurator__limits",
            children: limitComponents.map((LimitComponent, index) => {
              if (LimitComponent === null) return null;
              const schema = schemas[index];
              return /*#__PURE__*/_jsx("li", {
                className: "LimitsConfigurator__limit-item",
                children: /*#__PURE__*/_jsx(LimitComponent, {
                  defaultLimit: defaults[schema.name],
                  isSharedWithOther: sharedLimits && Object.hasOwn(sharedLimits, schema.name),
                  label: schema.label,
                  limit: (sharedLimits && sharedLimits[schema.name]) ?? limits[schema.name],
                  onChange: value => handleLimitChange(schema.name, value),
                  schema: schema
                })
              }, schema.name);
            })
          }), /*#__PURE__*/_jsxs(ModalActionBar, {
            children: [/*#__PURE__*/_jsx(Button, {
              onClick: onRequestClose,
              variant: "flat",
              children: "Cancel"
            }), /*#__PURE__*/_jsx(Button, {
              $ml: "1sp",
              busy: saving,
              onClick: handleSave,
              variant: "primary",
              disabled: validateRegions(),
              children: "Save"
            })]
          })]
        })]
      }), ExtendedEditor && /*#__PURE__*/_jsx("div", {
        className: "LimitsConfigurator__extended-pane",
        children: /*#__PURE__*/_jsx(ExtendedEditor, {
          ...extendedEditorProps
        })
      })]
    })
  });
}